import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { socket } from 'api';
import { data } from 'services/data';
import { notify } from 'libs/common';
import { Confirm, FilledButton } from 'libs/ui';
import { Controls } from './controls';
import { Preview } from './preview';
import { Editor } from './editor/editor';
import { Input } from './controls/input';
import { Label } from './controls/label';
import { prepareText } from './editor/editor.func';
import { Control } from './controls/control.styled';
import { Content, Header, Title, Inner, Left, Right, Center, CloseButton, NamePlace, Buttons, ButtonsRight } from './add-form.styled';

const r = '\n';
const minCols = 25;
const maxCols = 50;
const maxRows = 30;
const timeout = 1000;
// let isCopy = false;
let timer1;
let timer1first = true;

export const AddForm = (props) => {
  const { close, refresh, row } = props;
  const rowOk = row ?? { name: '', width: 42, body: '' };
  const [error, setError] = useState(false);
  const [name, setName] = useState(rowOk.name);
  const [tpl, setTpl] = useState(rowOk.body);
  const [cols, setCols] = useState(rowOk.width);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [isEdit, setIsEdit] = useState(row !== null);
  const [isCopy, setIsCopy] = useState(false);

  const scale = data.getScale();
  const { t } = useTranslation();
  const editorRef = useRef(null);
  useEffect(() => {
    /**
     * get test picture of ticket template
     */
    const test = () => {
      const out = {
        body: tpl,
        width: cols,
        mean_id: '1234567890',
        mean_number: '12',
        rack: 1,
        tariff: 1,
        // timestamp: (int = None),
        plate: 'A777AA00',
      };
      socket.call('receipt_template.test', out);
    };

    if (timer1) {
      clearTimeout(timer1);
    }

    if (tpl) {
      setLoading(true);
      timer1 = setTimeout(
        () => {
          timer1first = false;
          test();
        },
        timer1first ? 0 : timeout,
      );
    } else {
      setPreview('');
    }

    return () => {
      if (timer1) {
        clearTimeout(timer1);
      }
    };
  }, [tpl, cols]);

  const success = (edit) => {
    const msg = edit
      ? t('messages.Изменения сохранены Билеты будут печататься по обновленным параметрам, если шаблон был установлен на устройстве')
      : t('messages.Шаблон успешно создан');
    refresh();
    notify(t('windows.Внимание'), msg, 'success');
  };

  useEffect(() => {
    const testData = (msg) => {
      setPreview(msg);
      setLoading(false);
    };

    const onError = (msg) => {
      if (msg === 'Validation error') {
        setError(true);
        notify(t('windows.Внимание'), t('Шаблон с таким именем уже существует Пожалуйста, выберите другое имя'));
      }
    };

    const afterDelete = () => {
      refresh();
      notify(
        t('windows.Внимание'),
        t('messages.Шаблон удален Билеты будут печататься по системному шаблону, если шаблон был установлен на устройстве'),
        'success',
      );
    };

    const successEdit = () => {
      success(true);
    };

    const successAdd = () => {
      success(false);
    };

    socket.on('receipt_template.add', successAdd);
    socket.on('receipt_template.edit', successEdit);
    socket.on('receipt_template.test', testData);
    socket.on('receipt_template.delete', afterDelete);
    socket.on('error', onError);

    return () => {
      socket.off('receipt_template.add', successAdd);
      socket.off('receipt_template.edit', successEdit);
      socket.off('receipt_template.test', testData);
      socket.off('receipt_template.delete', afterDelete);
      socket.off('error', onError);
      timer1first = true;
      setIsCopy(false);
    };
  }, []);

  /**
   * add or edit template
   */
  const save = () => {
    setIsCopy(false);

    const out = {
      name,
      body: tpl,
      width: cols,
    };
    if (isEdit) {
      out.receipt_template_id = rowOk.receipt_template_id;
    }
    socket.call(isEdit ? 'receipt_template.edit' : 'receipt_template.add', out);
  };

  /**
   * create template copy
   */
  const copy = () => {
    setIsEdit(false);
    setName('');
    setIsCopy(true);
  };

  /**
   * del template
   */
  const del = () => {
    setConfirm(true);
  };

  /**
   * delete template
   */
  const delAction = () => {
    const out = {
      receipt_template_id: rowOk.receipt_template_id,
    };

    // isCopy = true;
    socket.call('receipt_template.delete', out);
    setConfirm(false);
  };

  const closeConfirm = () => {
    setConfirm(false);
  };

  /**
   * change editor tpl
   * @param {*} val
   */
  const change = (val) => {
    setTpl(prepareText(val, cols, maxRows));
  };

  /**
   * change from controls
   * @param {*} val
   */
  const changeFromControl = (val) => {
    let valOk = val;
    if (editorRef.current) {
      const input = editorRef.current;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      let out = input.value.substring(0, start);
      let last = input.value.substring(end);
      if (out.length && out[out.length - 1] !== r) {
        valOk = r + valOk;
      }
      if (last) {
        if (last[0] !== r) {
          valOk += r;
        }
      } else {
        valOk += r;
      }

      setTpl(prepareText(out + valOk + last, cols, maxRows));
      const newSelection = start + valOk.length;
      setTimeout(() => {
        input.selectionStart = newSelection; //start === end ? end + out.length : end;
        input.selectionEnd = newSelection; //start === end ? end + out.length : end;
        input.focus();
      }, 10);
    }
  };

  const changeName = (val) => {
    setError(false);
    setName(val);
  };

  /**
   * change editor width
   * @param {*} val
   */
  const changeCols = (val) => {
    val = parseInt(val);
    if (val < minCols) {
      val = minCols;
    }
    if (val > maxCols) {
      val = maxCols;
    }
    setCols(val);
  };

  const valid = name.trim() !== '' && tpl.trim() !== '';
  const title = isEdit ? t('windows.Изменить шаблон') : t('windows.Добавить шаблон');
  return (
    <>
      {createPortal(
        <Content style={{ transform: `scale(${scale}) translateX(-50%)` }}>
          <Header>
            <Title>{title}</Title>
            <CloseButton onClick={close}>
              <img src="./images/close_icon.png" alt="X" />
            </CloseButton>
          </Header>
          <Inner>
            <Left>
              <NamePlace>
                <Control>
                  <Label>{t('forms.Имя')}:</Label>
                  <Input $error={error} name="name" value={name} onChange={changeName} placeholder={t('forms.Название шаблона') + ' *'} />
                </Control>
              </NamePlace>
              <Controls cols={cols} changeCols={changeCols} minCols={minCols} maxCols={maxCols} onChange={changeFromControl} />
            </Left>
            <Center>
              <Editor value={tpl} onChange={change} cols={cols} maxRows={maxRows} ref={editorRef} />
            </Center>
            <Right>
              <Preview data={preview} loading={loading} />
            </Right>
          </Inner>
          <Buttons>
            <FilledButton className="size-sm" onClick={close}>
              {t('buttons.Отмена')}
            </FilledButton>
            <ButtonsRight>
              {isEdit && (
                <>
                  <div>
                    <FilledButton className="size-sm" color="danger" onClick={del}>
                      {t('buttons.Удалить')}
                    </FilledButton>
                    {confirm && (
                      <Confirm
                        above
                        arrowPos={'30px'}
                        close={closeConfirm}
                        action={delAction}
                        title={t('messages.Удалить шаблон билета') + '?'}
                        actionTitle={<>&nbsp; &nbsp; {t('buttons.Ок')} &nbsp; &nbsp;</>}
                      />
                    )}
                  </div>
                  <div>
                    <FilledButton className="size-sm" disabled={!valid} onClick={copy}>
                      {t('buttons.Копировать')}
                    </FilledButton>
                  </div>
                </>
              )}
              <FilledButton className="size-sm" color="success" disabled={!valid} onClick={save}>
                {t('buttons.Сохранить')}
              </FilledButton>
            </ButtonsRight>
          </Buttons>
        </Content>,
        document.body,
      )}
    </>
  );
};
